import React, { useState } from 'react';

export default function OffsiderWebsite() {
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedService, setSelectedService] = useState('');
  const [formData, setFormData] = useState({
    // Common fields
    name: '',
    email: '',
    phone: '',
    businessName: '',
    additionalInfo: '',
    preferContact: 'email',
    timeline: '',
    budget: '',
    
    // Website specific
    currentWebsite: '',
    likesAboutCurrent: '',
    dislikesAboutCurrent: '',
    websitesYouLike: '',
    targetAudience: '',
    websiteFeatures: [],
    
    // Documents specific
    documentTypes: [],
    documentVolume: '',
    hasExistingBranding: '',
    documentOtherDescription: '',
    documentScope: '',
    documentScopeDescription: '',
    
    // Logos & Branding specific
    businessDescription: '',
    hasCurrentLogo: '',
    stylePreferences: '',
    brandUsage: [],
    
    // Custom Apps specific
    problemToSolve: '',
    currentProcess: '',
    appUsers: '',
    appFeatures: '',
    integrations: '',
    
    // Automation specific
    taskToAutomate: '',
    currentManualSteps: '',
    softwareUsed: [],
    automationFrequency: '',
    softwareOther: '',
    automationLevel: '',
    
    // Database specific
    dataToTrack: '',
    currentDataSystem: '',
    whoNeedsAccess: '',
    reportsNeeded: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  // Helper function to navigate to form with a specific service pre-selected
  const goToServiceForm = (serviceId) => {
    setSelectedService(serviceId);
    setCurrentPage('project');
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch('https://formspree.io/f/xojjvzdl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, selectedService })
      });
      
      if (response.ok) {
        alert('Thanks! We\'ll be in touch within 1-2 business days.');
        setFormData({
          name: '', email: '', phone: '', businessName: '', additionalInfo: '',
          preferContact: 'email', timeline: '', budget: '',
          currentWebsite: '', likesAboutCurrent: '', dislikesAboutCurrent: '',
          websitesYouLike: '', targetAudience: '', websiteFeatures: [],
          documentTypes: [], documentVolume: '', hasExistingBranding: '',
          documentOtherDescription: '', documentScope: '', documentScopeDescription: '',
          businessDescription: '', hasCurrentLogo: '', stylePreferences: '', brandUsage: [],
          problemToSolve: '', currentProcess: '', appUsers: '', appFeatures: '', integrations: '',
          taskToAutomate: '', currentManualSteps: '', softwareUsed: [], automationFrequency: '',
          softwareOther: '', automationLevel: '',
          dataToTrack: '', currentDataSystem: '', whoNeedsAccess: '', reportsNeeded: '',
        });
        setSelectedService('');
        setCurrentPage('home');
      } else {
        alert('Something went wrong. Please try again or email us directly.');
      }
    } catch (error) {
      alert('Something went wrong. Please try again or email us directly.');
    }
  };

  const services = [
    { 
      id: 'website', 
      name: 'Website Design', 
      icon: '🖥️',
      color: '#059669',
      description: 'Modern, mobile-friendly websites',
      budgets: [
        { value: 'starter', label: 'Starter - $999', desc: '1-3 pages' },
        { value: 'growth', label: 'Growth - $2,499', desc: '5-10 pages' },
        { value: 'professional', label: 'Professional - $4,999', desc: '10+ pages' },
        { value: 'premium', label: 'Premium - $7,999+', desc: 'Custom build' },
      ]
    },
    { 
      id: 'documents', 
      name: 'Documents & Templates', 
      icon: '📄',
      color: '#2563eb',
      description: 'Professional business documents',
      budgets: []
    },
    { 
      id: 'branding', 
      name: 'Logos & Branding', 
      icon: '🎨',
      color: '#7c3aed',
      description: 'Visual identity & brand design',
      budgets: [
        { value: 'logo-only', label: '$299', desc: 'Logo only' },
        { value: 'starter-pack', label: '$499', desc: 'Logo + business cards' },
        { value: 'full-brand', label: '$999', desc: 'Full brand package' },
        { value: 'premium', label: '$1,499+', desc: 'Complete identity' },
      ]
    },
    { 
      id: 'apps', 
      name: 'Custom Apps', 
      icon: '📱',
      color: '#d97706',
      description: 'Purpose-built tools & portals',
      budgets: []
    },
    { 
      id: 'automation', 
      name: 'Automation', 
      icon: '⚡',
      color: '#0d9488',
      description: 'Workflow & process automation',
      budgets: []
    },
    { 
      id: 'database', 
      name: 'Databases & Reporting', 
      icon: '📊',
      color: '#e11d48',
      description: 'Data management & dashboards',
      budgets: []
    },
  ];

  const getSelectedServiceData = () => services.find(s => s.id === selectedService);

  const renderServiceQuestions = () => {
    switch(selectedService) {
      case 'website':
        return (
          <>
            <div className="bg-white rounded-2xl p-6 border border-stone-200">
              <h3 className="text-lg font-semibold text-stone-800 mb-4">About your current situation</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Do you have a current website?</label>
                  <input
                    type="text"
                    name="currentWebsite"
                    value={formData.currentWebsite}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                    placeholder="www.example.com (or 'No current website')"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">What do you like about your current site? (if applicable)</label>
                  <textarea
                    name="likesAboutCurrent"
                    value={formData.likesAboutCurrent}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-none"
                    placeholder="What's working well..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">What would you like to change or improve?</label>
                  <textarea
                    name="dislikesAboutCurrent"
                    value={formData.dislikesAboutCurrent}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-none"
                    placeholder="What's not working, what needs to change..."
                  />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-stone-200">
              <h3 className="text-lg font-semibold text-stone-800 mb-4">Your vision</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Any websites you like the look of?</label>
                  <textarea
                    name="websitesYouLike"
                    value={formData.websitesYouLike}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-none"
                    placeholder="Paste links to sites you like and tell us what you like about them..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Who are your customers?</label>
                  <textarea
                    name="targetAudience"
                    value={formData.targetAudience}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-none"
                    placeholder="Who visits your site, who do you want to reach..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-3">Features you need</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Contact form', 'Online booking', 'Photo gallery', 'Blog', 'Shop/payments', 'Customer login', 'Live chat', 'Newsletter signup'].map((feature) => (
                      <label key={feature} className={`p-2 rounded-lg border cursor-pointer transition-all text-sm flex items-center gap-2 ${formData.websiteFeatures.includes(feature) ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'}`}>
                        <input type="checkbox" checked={formData.websiteFeatures.includes(feature)} onChange={() => handleArrayChange('websiteFeatures', feature)} className="sr-only" />
                        <div className={`w-4 h-4 rounded flex items-center justify-center ${formData.websiteFeatures.includes(feature) ? 'bg-emerald-500' : 'border border-stone-300'}`}>
                          {formData.websiteFeatures.includes(feature) && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                        </div>
                        {feature}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        );

      case 'documents':
        return (
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Tell us about your document needs</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">What types of documents do you need?</label>
                <div className="grid grid-cols-2 gap-2">
                  {['SWMS / JSA', 'Contracts', 'Invoices', 'Quotes', 'HR policies', 'Checklists', 'Reports', 'Forms', 'Proposals', 'Other'].map((type) => (
                    <label key={type} className={`p-2 rounded-lg border cursor-pointer transition-all text-sm flex items-center gap-2 ${formData.documentTypes.includes(type) ? 'border-blue-500 bg-blue-50' : 'border-stone-200 hover:border-stone-300'}`}>
                      <input type="checkbox" checked={formData.documentTypes.includes(type)} onChange={() => handleArrayChange('documentTypes', type)} className="sr-only" />
                      <div className={`w-4 h-4 rounded flex items-center justify-center ${formData.documentTypes.includes(type) ? 'bg-blue-500' : 'border border-stone-300'}`}>
                        {formData.documentTypes.includes(type) && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                      </div>
                      {type}
                    </label>
                  ))}
                </div>
                {formData.documentTypes.includes('Other') && (
                  <div className="mt-3">
                    <textarea
                      name="documentOtherDescription"
                      value={formData.documentOtherDescription || ''}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none resize-none"
                      placeholder="Tell us what other documents you need..."
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">What are you looking for?</label>
                <div className="space-y-2">
                  {[
                    { value: 'single', label: 'Single template ($149)', desc: 'One specific document' },
                    { value: 'pack', label: 'Starter pack ($499)', desc: '5-10 related templates' },
                    { value: 'suite', label: 'Complete suite ($999)', desc: 'Full document system' },
                    { value: 'custom', label: 'Custom / not sure', desc: 'Let\'s discuss' },
                  ].map((option) => (
                    <label key={option.value} className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-3 ${formData.documentScope === option.value ? 'border-blue-500 bg-blue-50' : 'border-stone-200 hover:border-stone-300'}`}>
                      <input type="radio" name="documentScope" value={option.value} checked={formData.documentScope === option.value} onChange={handleInputChange} className="sr-only" />
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${formData.documentScope === option.value ? 'bg-blue-500' : 'border-2 border-stone-300'}`}>
                        {formData.documentScope === option.value && <div className="w-2 h-2 rounded-full bg-white"></div>}
                      </div>
                      <div>
                        <div className="font-medium text-stone-800">{option.label}</div>
                        <div className="text-sm text-stone-500">{option.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Do you have existing branding (logo, colours)?</label>
                <div className="flex gap-3">
                  {['Yes', 'No', 'Sort of'].map((option) => (
                    <label key={option} className={`px-4 py-2 rounded-full border cursor-pointer transition-all ${formData.hasExistingBranding === option ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-stone-200 hover:border-stone-300'}`}>
                      <input type="radio" name="hasExistingBranding" value={option} checked={formData.hasExistingBranding === option} onChange={handleInputChange} className="sr-only" />
                      {option}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'branding':
        return (
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Tell us about your brand</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Tell us about your business</label>
                <textarea
                  name="businessDescription"
                  value={formData.businessDescription}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none resize-none"
                  placeholder="What do you do? Who are your customers? What makes you different?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Do you have a current logo?</label>
                <div className="flex gap-3">
                  {['Yes - needs updating', 'Yes - starting fresh', 'No - first logo'].map((option) => (
                    <label key={option} className={`px-4 py-2 rounded-full border cursor-pointer transition-all text-sm ${formData.hasCurrentLogo === option ? 'border-purple-500 bg-purple-50 text-purple-800' : 'border-stone-200 hover:border-stone-300'}`}>
                      <input type="radio" name="hasCurrentLogo" value={option} checked={formData.hasCurrentLogo === option} onChange={handleInputChange} className="sr-only" />
                      {option}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Any style preferences?</label>
                <textarea
                  name="stylePreferences"
                  value={formData.stylePreferences}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none resize-none"
                  placeholder="Modern, classic, playful, professional... Share examples if you have them!"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">Where will you use your brand?</label>
                <div className="grid grid-cols-2 gap-2">
                  {['Website', 'Business cards', 'Social media', 'Signage/vehicles', 'Uniforms', 'Documents', 'Packaging', 'Ads/marketing'].map((usage) => (
                    <label key={usage} className={`p-2 rounded-lg border cursor-pointer transition-all text-sm flex items-center gap-2 ${formData.brandUsage.includes(usage) ? 'border-purple-500 bg-purple-50' : 'border-stone-200 hover:border-stone-300'}`}>
                      <input type="checkbox" checked={formData.brandUsage.includes(usage)} onChange={() => handleArrayChange('brandUsage', usage)} className="sr-only" />
                      <div className={`w-4 h-4 rounded flex items-center justify-center ${formData.brandUsage.includes(usage) ? 'bg-purple-500' : 'border border-stone-300'}`}>
                        {formData.brandUsage.includes(usage) && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                      </div>
                      {usage}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'apps':
        return (
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Tell us about your app needs</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">What problem are you trying to solve?</label>
                <textarea
                  name="problemToSolve"
                  value={formData.problemToSolve}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none resize-none"
                  placeholder="What's the pain point? What would make your life easier?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">How do you currently handle this?</label>
                <textarea
                  name="currentProcess"
                  value={formData.currentProcess}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none resize-none"
                  placeholder="Spreadsheets, paper, existing software..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Who will use this app?</label>
                <textarea
                  name="appUsers"
                  value={formData.appUsers}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none resize-none"
                  placeholder="Just you, your team, customers, public..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">What systems does it need to connect with?</label>
                <textarea
                  name="integrations"
                  value={formData.integrations}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none resize-none"
                  placeholder="Xero, your website, email, other software..."
                />
              </div>
            </div>
            <div className="mt-5 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>💡 Pricing note:</strong> Custom apps typically start from $2,999. We'll provide a detailed quote based on your specific requirements.
              </p>
            </div>
          </div>
        );

      case 'automation':
        return (
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Tell us about your automation needs</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">What task do you want to automate?</label>
                <textarea
                  name="taskToAutomate"
                  value={formData.taskToAutomate}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none resize-none"
                  placeholder="Sending invoices, updating spreadsheets, sending reminders..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">What are the manual steps currently?</label>
                <textarea
                  name="currentManualSteps"
                  value={formData.currentManualSteps}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none resize-none"
                  placeholder="1. Open email 2. Copy data 3. Paste into spreadsheet..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">What software do you currently use?</label>
                <div className="grid grid-cols-2 gap-2">
                  {['Excel/Sheets', 'Xero/MYOB', 'Outlook/Gmail', 'Slack/Teams', 'Trello/Asana', 'Dropbox/Drive', 'Salesforce/HubSpot', 'Other'].map((software) => (
                    <label key={software} className={`p-2 rounded-lg border cursor-pointer transition-all text-sm flex items-center gap-2 ${formData.softwareUsed.includes(software) ? 'border-teal-500 bg-teal-50' : 'border-stone-200 hover:border-stone-300'}`}>
                      <input type="checkbox" checked={formData.softwareUsed.includes(software)} onChange={() => handleArrayChange('softwareUsed', software)} className="sr-only" />
                      <div className={`w-4 h-4 rounded flex items-center justify-center ${formData.softwareUsed.includes(software) ? 'bg-teal-500' : 'border border-stone-300'}`}>
                        {formData.softwareUsed.includes(software) && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                      </div>
                      {software}
                    </label>
                  ))}
                </div>
                {formData.softwareUsed.includes('Other') && (
                  <div className="mt-3">
                    <textarea
                      name="softwareOther"
                      value={formData.softwareOther || ''}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none resize-none"
                      placeholder="What other software do you use?"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">How often does this task happen?</label>
                <div className="flex flex-wrap gap-2">
                  {['Multiple times daily', 'Daily', 'Weekly', 'Monthly', 'As needed'].map((freq) => (
                    <label key={freq} className={`px-4 py-2 rounded-full border cursor-pointer transition-all text-sm ${formData.automationFrequency === freq ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-stone-200 hover:border-stone-300'}`}>
                      <input type="radio" name="automationFrequency" value={freq} checked={formData.automationFrequency === freq} onChange={handleInputChange} className="sr-only" />
                      {freq}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 p-4 bg-teal-50 rounded-xl border border-teal-200">
              <p className="text-sm text-teal-800">
                <strong>💡 Pricing note:</strong> Simple automations start from $799. Complex workflows are quoted based on requirements.
              </p>
            </div>
          </div>
        );

      case 'database':
        return (
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Tell us about your data needs</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">What do you need to track or manage?</label>
                <textarea
                  name="dataToTrack"
                  value={formData.dataToTrack}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none resize-none"
                  placeholder="Customers, inventory, jobs, sales, employees..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">How do you currently manage this data?</label>
                <textarea
                  name="currentDataSystem"
                  value={formData.currentDataSystem}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none resize-none"
                  placeholder="Spreadsheets, paper, existing software, nothing yet..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Who needs access?</label>
                <textarea
                  name="whoNeedsAccess"
                  value={formData.whoNeedsAccess}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none resize-none"
                  placeholder="Just you, specific team members, whole company..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">What reports or insights do you need?</label>
                <textarea
                  name="reportsNeeded"
                  value={formData.reportsNeeded}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none resize-none"
                  placeholder="Sales by month, job status, overdue invoices, KPIs..."
                />
              </div>
            </div>
            <div className="mt-5 p-4 bg-rose-50 rounded-xl border border-rose-200">
              <p className="text-sm text-rose-800">
                <strong>💡 Pricing note:</strong> Database systems typically start from $1,499. We'll provide a detailed quote based on your requirements.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Scroll to section helper for services page
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  
  return (
    <div className="min-h-screen bg-stone-50" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Fraunces:wght@600;700&display=swap" rel="stylesheet" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-stone-50/90 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => { setCurrentPage('home'); setSelectedService(''); }} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #7cb77c 100%)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 3C12 3 8 7 8 12C8 15.5 9.5 18 12 20C14.5 18 16 15.5 16 12C16 7 12 3 12 3Z" fill="white" opacity="0.9"/>
                <path d="M12 8C12 8 10 10.5 10 13C10 14.8 10.8 16.2 12 17C13.2 16.2 14 14.8 14 13C14 10.5 12 8 12 8Z" fill="#2d4a35"/>
              </svg>
            </div>
            <span className="text-xl font-semibold text-stone-800" style={{ fontFamily: "'Fraunces', serif" }}>Offsider HQ</span>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            <button 
              onClick={() => { setCurrentPage('services'); window.scrollTo(0, 0); }} 
              className={`transition-colors ${currentPage === 'services' ? 'text-emerald-700 font-medium' : 'text-stone-600 hover:text-stone-900'}`}
            >
              Services
            </button>
            <button 
              onClick={() => { setCurrentPage('home'); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100); }} 
              className="text-stone-600 hover:text-stone-900 transition-colors"
            >
              Pricing
            </button>
            <button 
              onClick={() => { setCurrentPage('home'); setTimeout(() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }), 100); }} 
              className="text-stone-600 hover:text-stone-900 transition-colors"
            >
              About
            </button>
            <a 
              href="https://portal.offsiderhq.com/login"
              className="text-stone-600 hover:text-stone-900 transition-colors"
            >
              Client Login
            </a>
            <button 
              onClick={() => setCurrentPage('project')} 
              className="px-5 py-2.5 rounded-full text-white font-medium transition-all hover:shadow-lg hover:-translate-y-0.5" 
              style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #5d8a64 100%)' }}
            >
              Start a project
            </button>
          </div>
        </div>
      </nav>

      {/* ==================== HOME PAGE ==================== */}
      {currentPage === 'home' && (
        <>
          {/* Hero Section */}
          <section className="pt-32 pb-20 px-6 relative overflow-hidden">
            <div className="absolute top-20 right-0 w-96 h-96 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, #7cb77c 0%, transparent 70%)' }}></div>
            <div className="absolute bottom-0 left-10 w-64 h-64 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #4a7c59 0%, transparent 70%)' }}></div>
            
            <div className="max-w-6xl mx-auto relative">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-6">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  Help us help you
                </div>
                
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-stone-800 leading-tight mb-6" style={{ fontFamily: "'Fraunces', serif" }}>
                  Tech that{' '}
                  <span className="relative">
                    <span className="relative z-10" style={{ color: '#4a7c59' }}>just works</span>
                    <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                      <path d="M2 8C50 2 150 2 198 8" stroke="#7cb77c" strokeWidth="4" strokeLinecap="round" opacity="0.4"/>
                    </svg>
                  </span>
                </h1>
                
                <p className="text-xl text-stone-600 mb-10 leading-relaxed max-w-2xl">
                  Websites, apps, automations, and business documents built for tradies and small businesses. Practical tools that make your work easier—without the agency price tag.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <button onClick={() => setCurrentPage('project')} className="px-8 py-4 rounded-full text-white font-semibold text-lg transition-all hover:shadow-xl hover:-translate-y-1" style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #5d8a64 100%)' }}>
                    Start a project
                  </button>
                  <button onClick={() => setCurrentPage('services')} className="px-8 py-4 rounded-full border-2 border-stone-300 text-stone-700 font-semibold text-lg hover:border-stone-400 hover:bg-stone-100 transition-all">
                    See what we do
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Trust bar */}
          <section className="py-8 px-6 border-y border-stone-200 bg-white">
            <div className="max-w-6xl mx-auto flex flex-wrap justify-center items-center gap-8 md:gap-16 text-stone-400 text-sm">
              {['Australian owned', 'Real support from real people', 'Fair, transparent pricing', 'Built to last'].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Services Preview - How can we help */}
          <section id="services" className="py-24 px-6 bg-stone-100">
            <div className="max-w-6xl mx-auto">
              {/* Rounded green banner */}
              <div className="rounded-t-3xl py-16 px-6 text-center" style={{ background: 'linear-gradient(135deg, #2d4a35 0%, #4a7c59 100%)' }}>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Fraunces', serif" }}>How can we help?</h2>
                <p className="text-xl text-white/80 max-w-2xl mx-auto">Everything your business needs to look professional and run smoothly—without the big agency price tag.</p>
              </div>
              
              {/* White cards section */}
              <div className="rounded-b-3xl py-12 px-6 bg-white shadow-xl">
                <div className="max-w-5xl mx-auto">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[
                      { id: 'website', icon: '🖥️', name: 'Website Design', desc: 'Get found online with a professional site that looks great on any device and turns visitors into customers.', price: 'From $999' },
                      { id: 'documents', icon: '📄', name: 'Documents & Templates', desc: 'SWMS, contracts, invoices, checklists and more—professionally designed and branded to your business.', price: 'From $149' },
                      { id: 'branding', icon: '🎨', name: 'Logos & Branding', desc: 'Stand out with a professional logo and cohesive brand identity that builds trust and recognition.', price: 'From $299' },
                      { id: 'apps', icon: '💻', name: 'Custom Apps', desc: 'Calculators, client portals, booking systems—purpose-built tools designed around how you actually work.', price: 'From $2,999' },
                      { id: 'automation', icon: '⚡', name: 'Automation', desc: 'Connect your apps, extract data from PDFs, send auto-reminders—stop doing repetitive tasks manually.', price: 'From $799' },
                      { id: 'database', icon: '📊', name: 'Databases & Reporting', desc: 'Track jobs, customers, and inventory in one place. Get dashboards and reports that help you make decisions.', price: 'From $1,499' },
                    ].map((service, i) => (
                      <button
                        key={i}
                        onClick={() => goToServiceForm(service.id)}
                        className="group p-6 rounded-2xl bg-stone-50 border-2 border-stone-100 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 text-left"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' }}>
                            {service.icon}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-stone-800 mb-1">{service.name}</h3>
                            <p className="text-stone-500 text-sm mb-3">{service.desc}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-emerald-700 font-semibold text-sm">{service.price}</span>
                              <span className="text-emerald-600 font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity">Learn more →</span>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="text-center mt-10">
                    <button 
                      onClick={() => { setCurrentPage('services'); window.scrollTo(0, 0); }}
                      className="text-emerald-700 font-medium hover:underline"
                    >
                      View detailed service information →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section - Website Packages */}
          <section id="pricing" className="py-24 px-6 bg-white">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4" style={{ fontFamily: "'Fraunces', serif" }}>Website packages</h2>
                <p className="text-xl text-stone-500 max-w-2xl mx-auto">Transparent pricing. No hidden fees. Pick what fits.</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Starter */}
                <div className="p-6 rounded-2xl bg-stone-50 border-2 border-stone-200 hover:border-stone-300 transition-all">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">Starter</span>
                      <div className="text-3xl font-bold text-stone-800 mt-1">$999</div>
                    </div>
                    <div className="px-3 py-1 bg-stone-200 rounded-full text-xs font-medium text-stone-600">1–3 pages</div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Mobile-friendly', 'Contact form', 'Google Maps', 'Basic SEO'].map((feature) => (
                      <span key={feature} className="px-3 py-1 bg-white rounded-full text-xs text-stone-600 border border-stone-200">{feature}</span>
                    ))}
                  </div>
                  <button onClick={() => goToServiceForm('website')} className="w-full py-2.5 rounded-full border-2 border-stone-300 text-stone-700 font-medium hover:bg-stone-100 transition-all text-sm">
                    Get started
                  </button>
                </div>

                {/* Growth */}
                <div className="p-6 rounded-2xl bg-stone-50 border-2 border-stone-200 hover:border-stone-300 transition-all">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Growth</span>
                      <div className="text-3xl font-bold text-stone-800 mt-1">$2,499</div>
                    </div>
                    <div className="px-3 py-1 bg-blue-100 rounded-full text-xs font-medium text-blue-700">5–10 pages</div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Custom design', 'CMS included', 'Blog section', 'Analytics setup'].map((feature) => (
                      <span key={feature} className="px-3 py-1 bg-white rounded-full text-xs text-stone-600 border border-stone-200">{feature}</span>
                    ))}
                  </div>
                  <button onClick={() => goToServiceForm('website')} className="w-full py-2.5 rounded-full border-2 border-stone-300 text-stone-700 font-medium hover:bg-stone-100 transition-all text-sm">
                    Get started
                  </button>
                </div>

                {/* Professional - Popular */}
                <div className="p-6 rounded-2xl border-2 relative" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#4a7c59' }}>
                  <div className="absolute -top-3 left-4 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: '#4a7c59' }}>
                    Most popular
                  </div>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">Professional</span>
                      <div className="text-3xl font-bold text-stone-800 mt-1">$4,999</div>
                    </div>
                    <div className="px-3 py-1 bg-emerald-200 rounded-full text-xs font-medium text-emerald-800">10+ pages</div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Premium design', 'Animations', 'Booking system', 'Priority support'].map((feature) => (
                      <span key={feature} className="px-3 py-1 bg-white/70 rounded-full text-xs text-stone-700 border border-emerald-200">{feature}</span>
                    ))}
                  </div>
                  <button onClick={() => goToServiceForm('website')} className="w-full py-2.5 rounded-full text-white font-medium transition-all hover:shadow-lg text-sm" style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #5d8a64 100%)' }}>
                    Get started
                  </button>
                </div>

                {/* Premium */}
                <div className="p-6 rounded-2xl bg-stone-800 text-white">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-sm font-semibold text-emerald-400 uppercase tracking-wide">Premium</span>
                      <div className="text-3xl font-bold mt-1">$7,999</div>
                    </div>
                    <div className="px-3 py-1 bg-stone-700 rounded-full text-xs font-medium text-stone-300">Custom build</div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Admin portal', 'User accounts', 'E-commerce', 'Database integration'].map((feature) => (
                      <span key={feature} className="px-3 py-1 bg-stone-700 rounded-full text-xs text-stone-300">{feature}</span>
                    ))}
                  </div>
                  <button onClick={() => goToServiceForm('website')} className="w-full py-2.5 rounded-full bg-white text-stone-800 font-medium hover:bg-stone-100 transition-all text-sm">
                    Let's talk
                  </button>
                </div>
              </div>

              {/* Hosting add-on */}
              <div className="mt-8 p-6 rounded-2xl bg-stone-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-stone-800 mb-1">Ongoing hosting & maintenance</h3>
                  <p className="text-stone-500 text-sm">Updates, security, backups, and support when you need it.</p>
                </div>
                <div className="text-center sm:text-right flex-shrink-0">
                  <div className="text-2xl font-bold text-stone-800">$49<span className="text-base font-normal text-stone-500">/month</span></div>
                </div>
              </div>
            </div>
          </section>

          {/* About Section */}
          <section id="about" className="py-24 px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4" style={{ fontFamily: "'Fraunces', serif" }}>
                  A bit about us
                </h2>
                <p className="text-xl text-stone-500 max-w-2xl mx-auto">
                  We've spent years working across construction, logistics, hospitality, and more. We know what it's like to need tools that actually help—not fancy software that looks good in a demo.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <div className="text-center p-8 rounded-3xl bg-white border border-stone-200">
                  <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6" style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' }}>
                    <svg className="w-8 h-8 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-stone-800 mb-3">Straight talk</h3>
                  <p className="text-stone-500">Clear communication from start to finish. We explain everything in plain English and give you honest advice every step of the way.</p>
                </div>
                
                <div className="text-center p-8 rounded-3xl bg-white border border-stone-200">
                  <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6" style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' }}>
                    <svg className="w-8 h-8 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-stone-800 mb-3">Fair pricing</h3>
                  <p className="text-stone-500">What you see is what you pay. Clear quotes upfront, no hidden fees, and no surprise invoices down the track.</p>
                </div>
                
                <div className="text-center p-8 rounded-3xl bg-white border border-stone-200">
                  <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6" style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' }}>
                    <svg className="w-8 h-8 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-stone-800 mb-3">Built to last</h3>
                  <p className="text-stone-500">We take pride in doing things properly. Quality work you can rely on, with ongoing support whenever you need it.</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-4 gap-6">
                <div className="p-6 rounded-2xl bg-white border border-stone-200 text-center">
                  <div className="text-3xl font-bold text-emerald-700 mb-1">15+</div>
                  <div className="text-stone-500">Years experience</div>
                </div>
                <div className="p-6 rounded-2xl bg-white border border-stone-200 text-center">
                  <div className="text-3xl font-bold text-emerald-700 mb-1">100%</div>
                  <div className="text-stone-500">Australian owned</div>
                </div>
                <div className="p-6 rounded-2xl bg-white border border-stone-200 text-center">
                  <div className="text-3xl font-bold text-emerald-700 mb-1">1-1</div>
                  <div className="text-stone-500">Direct support</div>
                </div>
                <div className="p-6 rounded-2xl bg-white border border-stone-200 text-center">
                  <div className="text-3xl font-bold text-emerald-700 mb-1">24hr</div>
                  <div className="text-stone-500">Response time</div>
                </div>
              </div>
            </div>
          </section>

          {/* Industries Experience Section */}
          <section className="py-20 px-6 bg-stone-100">
            <div className="max-w-5xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4" style={{ fontFamily: "'Fraunces', serif" }}>Industries we've worked in</h2>
              <p className="text-stone-500 mb-10 max-w-2xl mx-auto">Our team brings real-world experience across a wide range of industries—so we understand the challenges you face.</p>
              
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  { name: 'Retail', icon: '🛍️' },
                  { name: 'Hospitality', icon: '🍽️' },
                  { name: 'Construction', icon: '🏗️' },
                  { name: 'Waste Management', icon: '♻️' },
                  { name: 'Finance', icon: '💰' },
                  { name: 'Graphic Design', icon: '🎨' },
                  { name: 'Web Development', icon: '💻' },
                  { name: 'Game Development', icon: '🎮' },
                  { name: 'Workplace Health & Safety', icon: '🦺' },
                ].map((industry) => (
                  <div 
                    key={industry.name}
                    className="px-5 py-3 bg-white rounded-full border border-stone-200 shadow-sm flex items-center gap-2 hover:shadow-md hover:border-stone-300 transition-all"
                  >
                    <span className="text-lg">{industry.icon}</span>
                    <span className="text-stone-700 font-medium">{industry.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-24 px-6" style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #3d6b4a 100%)' }}>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Fraunces', serif" }}>
                Ready to get started?
              </h2>
              <p className="text-xl text-emerald-100 mb-10 max-w-2xl mx-auto">
                Let's have a chat about what you need. No pressure—just a friendly conversation about how we can help.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button onClick={() => setCurrentPage('project')} className="px-8 py-4 rounded-full bg-white text-emerald-800 font-semibold text-lg hover:shadow-xl transition-all">
                  Start a project
                </button>
                <a href="mailto:hello@offsiderhq.com" className="px-8 py-4 rounded-full border-2 border-white text-white font-semibold text-lg hover:bg-white/10 transition-all">
                  hello@offsiderhq.com
                </a>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ==================== SERVICES PAGE ==================== */}
      {currentPage === 'services' && (
        <>
          {/* Services Header */}
          <section className="pt-28 pb-8 px-6 bg-stone-50">
            <div className="max-w-6xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4" style={{ fontFamily: "'Fraunces', serif" }}>
                Our Services
              </h1>
              <p className="text-xl text-stone-500 max-w-2xl mx-auto">
                From websites to automation, we build digital tools that help small businesses work smarter.
              </p>
            </div>
          </section>

          {/* Quick Jump Navigation */}
          <section className="py-4 px-6 border-b border-stone-200 bg-white sticky top-[73px] z-40 shadow-sm">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  { id: 'websites', label: 'Websites', icon: '🖥️' },
                  { id: 'branding', label: 'Branding', icon: '🎨' },
                  { id: 'documents', label: 'Documents', icon: '📄' },
                  { id: 'automation', label: 'Automation', icon: '⚡' },
                  { id: 'apps', label: 'Custom Apps', icon: '💻' },
                  { id: 'databases', label: 'Databases', icon: '📊' },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="px-4 py-2 rounded-full text-sm font-medium text-stone-600 hover:bg-emerald-50 hover:text-emerald-700 transition-all flex items-center gap-2"
                  >
                    <span>{item.icon}</span>
                    <span className="hidden sm:inline">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* WEBSITES SECTION */}
          <section id="websites" className="py-20 px-6 scroll-mt-32 bg-stone-50">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Visual Side */}
                <div className="relative">
                  <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ background: 'linear-gradient(135deg, #2d4a35 0%, #4a7c59 100%)' }}>
                    <div className="p-8">
                      <div className="absolute top-6 right-6 px-4 py-2 rounded-xl bg-white/20 backdrop-blur-sm">
                        <p className="text-white/80 text-xs">Websites from</p>
                        <p className="text-white text-2xl font-bold">$999</p>
                      </div>
                      <div className="mt-8 bg-white rounded-xl shadow-xl overflow-hidden">
                        <div className="h-8 bg-stone-100 flex items-center px-3 gap-2 border-b border-stone-200">
                          <div className="flex gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-400"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                            <div className="w-3 h-3 rounded-full bg-green-400"></div>
                          </div>
                          <div className="flex-1 mx-4">
                            <div className="bg-white rounded px-3 py-1 text-xs text-stone-400 max-w-xs">offsiderhq.com</div>
                          </div>
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #7cb77c 100%)' }}>
                              <div className="w-5 h-5 rounded-full bg-white"></div>
                            </div>
                            <span className="font-semibold text-stone-800">Offsider HQ</span>
                          </div>
                          <h3 className="text-xl font-bold text-stone-800 mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Digital tools for small business</h3>
                          <p className="text-stone-500 text-sm mb-4">Websites, branding, documents, and automation — built for tradies and small businesses.</p>
                          <div className="flex gap-3">
                            <span className="px-4 py-2 rounded-full text-white text-sm font-medium" style={{ background: '#4a7c59' }}>Start a project</span>
                            <span className="px-4 py-2 rounded-full border border-stone-300 text-stone-600 text-sm font-medium">See services</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-white/60 text-sm mt-6 text-center">Mobile-friendly sites that look great and work even better</p>
                    </div>
                  </div>
                </div>

                {/* Content Side */}
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-4">Websites</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6" style={{ fontFamily: "'Fraunces', serif" }}>
                    Clean, modern websites that actually work
                  </h2>
                  <p className="text-lg text-stone-600 mb-8">
                    No cookie-cutter templates. We build custom websites that look professional, load fast, and help your customers find what they need. Mobile-friendly and easy for you to update.
                  </p>
                  
                  <div className="space-y-3 mb-8">
                    {['Custom design tailored to your brand', 'Mobile-responsive on all devices', 'Fast loading speeds', 'Easy content management', 'Basic SEO setup included', 'Ongoing support available'].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                        <span className="text-stone-700">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex flex-wrap gap-3 mb-6">
                    {[{ tier: 'Starter', price: '$999' }, { tier: 'Business', price: '$2,499' }, { tier: 'Professional', price: '$4,999' }, { tier: 'Premium', price: '$7,999+' }].map((item, i) => (
                      <div key={i} className="px-4 py-2 rounded-xl bg-stone-100 border border-stone-200">
                        <p className="text-xs text-stone-500">{item.tier}</p>
                        <p className="text-lg font-bold text-stone-800">{item.price}</p>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => goToServiceForm('website')}
                    className="px-6 py-3 rounded-full text-white font-medium transition-all hover:shadow-lg hover:-translate-y-0.5" 
                    style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #5d8a64 100%)' }}
                  >
                    Get a website quote →
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* BRANDING SECTION */}
          <section id="branding" className="py-20 px-6 scroll-mt-32 bg-white">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Content Side */}
                <div className="order-2 lg:order-1">
                  <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-4">Logos & Branding</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6" style={{ fontFamily: "'Fraunces', serif" }}>
                    Professional identity for your business
                  </h2>
                  <p className="text-lg text-stone-600 mb-8">
                    Look professional without the agency price tag. We create logos and brand identities that capture who you are and help you stand out.
                  </p>
                  
                  <div className="space-y-3 mb-8">
                    {['Custom logo design with multiple concepts', 'Colour palette and typography', 'Brand guidelines document', 'Business card and letterhead designs', 'Social media profile graphics', 'All files in print and web formats'].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                        <span className="text-stone-700">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex flex-wrap gap-3 mb-6">
                    {[{ tier: 'Logo Only', price: '$299' }, { tier: 'Logo + Colours', price: '$499' }, { tier: 'Full Brand Kit', price: '$999' }, { tier: 'Complete Identity', price: '$1,499+' }].map((item, i) => (
                      <div key={i} className="px-4 py-2 rounded-xl bg-stone-100 border border-stone-200">
                        <p className="text-xs text-stone-500">{item.tier}</p>
                        <p className="text-lg font-bold text-stone-800">{item.price}</p>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => goToServiceForm('branding')}
                    className="px-6 py-3 rounded-full text-white font-medium transition-all hover:shadow-lg hover:-translate-y-0.5" 
                    style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #5d8a64 100%)' }}
                  >
                    Get a branding quote →
                  </button>
                </div>

                {/* Visual Side */}
                <div className="order-1 lg:order-2 relative">
                  <div className="bg-stone-100 rounded-3xl p-8 relative">
                    <div className="relative h-64 mb-8">
                      <div className="absolute left-4 top-4 w-56 h-32 rounded-xl shadow-lg transform -rotate-6" style={{ background: 'linear-gradient(135deg, #2d4a35 0%, #4a7c59 100%)' }}></div>
                      <div className="absolute left-12 top-8 w-56 h-32 bg-white rounded-xl shadow-xl p-4 transform rotate-2 z-10">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #7cb77c 100%)' }}>
                            <div className="w-4 h-4 rounded-full bg-white"></div>
                          </div>
                          <span className="font-semibold text-stone-800 text-sm">Offsider HQ</span>
                        </div>
                        <div className="mt-auto">
                          <p className="text-stone-800 font-medium text-sm">Your Name</p>
                          <p className="text-stone-500 text-xs">hello@offsiderhq.com</p>
                          <p className="text-stone-500 text-xs">0400 000 000</p>
                        </div>
                      </div>
                      <div className="absolute right-0 top-0 w-48 rounded-2xl shadow-xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #2d4a35 0%, #4a7c59 100%)' }}>
                        <div className="p-4 text-white">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                              <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                            </div>
                            <span className="font-medium text-xs">Offsider HQ</span>
                          </div>
                          <h4 className="font-bold text-sm mb-2">Digital services for small business</h4>
                          <div className="space-y-1 text-xs text-white/80">
                            <p>✓ Websites from $999</p>
                            <p>✓ Logos from $299</p>
                            <p>✓ Documents from $149</p>
                          </div>
                          <div className="mt-3 w-full py-1.5 rounded-full bg-white/20 text-white text-xs font-medium text-center">
                            Get a free quote →
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center gap-2">
                      {[{ tier: 'Logo Only', price: '$299' }, { tier: 'Logo + Colours', price: '$499' }, { tier: 'Full Brand Kit', price: '$999' }, { tier: 'Complete Identity', price: '$1,499+' }].map((item, i) => (
                        <div key={i} className="px-3 py-2 rounded-lg bg-white shadow-sm text-center">
                          <p className="text-stone-500 text-[10px]">{item.tier}</p>
                          <p className="text-emerald-700 font-bold text-sm">{item.price}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* DOCUMENTS SECTION */}
          <section id="documents" className="py-20 px-6 scroll-mt-32 bg-stone-50">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Visual Side */}
                <div className="relative">
                  <div className="bg-stone-100 rounded-3xl p-8">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-stone-800" style={{ fontFamily: "'Fraunces', serif" }}>Professional Documents & Templates</h3>
                        <p className="text-stone-500 text-sm">Ready-to-use templates for Australian small businesses</p>
                      </div>
                      <div className="px-4 py-2 rounded-full text-white font-medium text-sm" style={{ background: '#4a7c59' }}>
                        From $149
                      </div>
                    </div>
                    <div className="flex justify-center gap-4 mb-6">
                      {/* Document cards */}
                      <div className="w-36 bg-white rounded-xl shadow-lg overflow-hidden transform -rotate-3 hover:rotate-0 transition-transform">
                        <div className="h-8 flex items-center px-3" style={{ background: 'linear-gradient(135deg, #2d4a35 0%, #4a7c59 100%)' }}>
                          <div className="w-4 h-4 rounded bg-white/30"></div>
                        </div>
                        <div className="p-3">
                          <p className="text-[10px] font-semibold text-stone-800 mb-2">Safe Work Method Statement</p>
                          <div className="h-1 bg-emerald-500 rounded w-12 mb-2"></div>
                          <div className="space-y-1">
                            <div className="h-0.5 bg-stone-200 rounded"></div>
                            <div className="h-0.5 bg-stone-200 rounded w-3/4"></div>
                          </div>
                        </div>
                      </div>
                      <div className="w-36 bg-white rounded-xl shadow-lg overflow-hidden hover:scale-105 transition-transform z-10">
                        <div className="h-8 flex items-center px-3" style={{ background: 'linear-gradient(135deg, #2d4a35 0%, #4a7c59 100%)' }}>
                          <div className="w-4 h-4 rounded bg-white/30"></div>
                        </div>
                        <div className="p-3">
                          <p className="text-[10px] font-semibold text-stone-800 mb-2">Site Induction Checklist</p>
                          <div className="h-1 bg-emerald-500 rounded w-10 mb-2"></div>
                          <div className="space-y-1.5">
                            {[1,2,3,4].map((_, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <div className={`w-2.5 h-2.5 rounded border flex items-center justify-center ${i < 2 ? 'bg-emerald-500 border-emerald-500' : 'border-stone-300'}`}>
                                  {i < 2 && <span className="text-white text-[6px]">✓</span>}
                                </div>
                                <div className="h-0.5 bg-stone-200 rounded flex-1"></div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="w-36 bg-white rounded-xl shadow-lg overflow-hidden transform rotate-3 hover:rotate-0 transition-transform">
                        <div className="h-8 flex items-center px-3" style={{ background: 'linear-gradient(135deg, #2d4a35 0%, #4a7c59 100%)' }}>
                          <div className="w-4 h-4 rounded bg-white/30"></div>
                        </div>
                        <div className="p-3">
                          <p className="text-[10px] font-semibold text-stone-800 mb-2">Tax Invoice</p>
                          <div className="flex gap-1 mb-2">
                            <div className="h-1 bg-emerald-500 rounded w-6"></div>
                            <div className="h-1 bg-emerald-300 rounded w-4"></div>
                          </div>
                          <div className="flex justify-between items-center bg-emerald-50 rounded p-1">
                            <span className="text-[6px] text-stone-600">Total</span>
                            <span className="text-[8px] font-bold text-emerald-600">$0.00</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                      {[{ emoji: '📋', label: 'Safety & WHS' }, { emoji: '📑', label: 'Contracts' }, { emoji: '📁', label: 'HR & Policies' }, { emoji: '🧾', label: 'Invoices & Quotes' }, { emoji: '📊', label: 'Reports' }].map((cat, i) => (
                        <span key={i} className="px-3 py-1.5 rounded-full bg-white shadow-sm text-xs font-medium text-stone-600 flex items-center gap-1 border border-stone-100">
                          {cat.emoji} {cat.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Content Side */}
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-4">Documents & Templates</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6" style={{ fontFamily: "'Fraunces', serif" }}>
                    Professional templates ready to use
                  </h2>
                  <p className="text-lg text-stone-600 mb-8">
                    Stop wasting time creating documents from scratch. Get professionally designed templates that look great and meet Australian standards.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {['SWMS & JSAs', 'Site induction forms', 'Contracts & agreements', 'Invoices & quotes', 'HR policies', 'Incident reports', 'Timesheets', 'Checklists'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                        <span className="text-stone-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex flex-wrap gap-3 mb-6">
                    {[{ tier: 'Single Template', price: '$149' }, { tier: 'Starter Pack', price: '$499' }, { tier: 'Complete Suite', price: '$999' }].map((item, i) => (
                      <div key={i} className="px-4 py-2 rounded-xl bg-stone-100 border border-stone-200">
                        <p className="text-xs text-stone-500">{item.tier}</p>
                        <p className="text-lg font-bold text-stone-800">{item.price}</p>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => goToServiceForm('documents')}
                    className="px-6 py-3 rounded-full text-white font-medium transition-all hover:shadow-lg hover:-translate-y-0.5" 
                    style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #5d8a64 100%)' }}
                  >
                    Get a documents quote →
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* AUTOMATION SECTION */}
          <section id="automation" className="py-20 px-6 scroll-mt-32 bg-white">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Content Side */}
                <div className="order-2 lg:order-1">
                  <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium mb-4">Automation</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6" style={{ fontFamily: "'Fraunces', serif" }}>
                    Stop doing repetitive tasks manually
                  </h2>
                  <p className="text-lg text-stone-600 mb-8">
                    Connect your apps, extract data from PDFs, and automate your workflows. Tell us what's eating up your time and we'll build a solution that gives you hours back every week.
                  </p>
                  
                  {/* Tier Examples */}
                  <div className="space-y-6 mb-8">
                    {/* Simple */}
                    <div className="p-4 rounded-xl bg-stone-50 border border-stone-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-stone-800">Simple Automations</h4>
                        <span className="text-sm font-medium text-emerald-700">$799 – $1,499</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-stone-600">
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>New lead alerts (SMS + email)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>Appointment reminders</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>Auto review requests</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>Invoice reminders</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Medium */}
                    <div className="p-4 rounded-xl bg-stone-50 border border-stone-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-stone-800">Medium Workflows</h4>
                        <span className="text-sm font-medium text-emerald-700">$1,499 – $2,999</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-stone-600">
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>Quote follow-up sequences</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>PDF data extraction</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>Customer status updates</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>Auto document generation</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Complex */}
                    <div className="p-4 rounded-xl bg-stone-50 border border-stone-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-stone-800">Complex Integrations</h4>
                        <span className="text-sm font-medium text-emerald-700">$2,999+</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-stone-600">
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>Full customer journey</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>Multi-system sync</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>Automated quoting</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-500">→</span>
                          <span>Weekly auto-reports</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => goToServiceForm('automation')}
                    className="px-6 py-3 rounded-full text-white font-medium transition-all hover:shadow-lg hover:-translate-y-0.5" 
                    style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #5d8a64 100%)' }}
                  >
                    Tell us what you want to automate →
                  </button>
                </div>

                {/* Visual Side */}
                <div className="order-1 lg:order-2 relative">
                  <div className="bg-stone-100 rounded-3xl p-8">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-stone-800" style={{ fontFamily: "'Fraunces', serif" }}>Workflow Automation</h3>
                        <p className="text-stone-500 text-sm">Connect your tools, save hours every week</p>
                      </div>
                      <div className="px-4 py-2 rounded-full text-white font-medium text-sm" style={{ background: '#4a7c59' }}>
                        From $799
                      </div>
                    </div>
                    
                    {/* Automation Flow Diagram */}
                    <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
                      {/* Trigger */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-xl">📄</div>
                        <div className="flex-1">
                          <p className="text-xs text-stone-400 uppercase tracking-wide">Trigger</p>
                          <p className="text-sm font-medium text-stone-800">PDF invoice received</p>
                        </div>
                      </div>
                      
                      {/* Arrow */}
                      <div className="flex justify-center my-2">
                        <div className="w-0.5 h-6 bg-emerald-300 relative">
                          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent border-t-emerald-300"></div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-stone-800">Extract invoice data</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-stone-800">Add to Xero / spreadsheet</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-stone-800">Send confirmation email</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* App Icons */}
                    <div className="flex flex-wrap justify-center gap-3">
                      {[
                        { name: 'Xero', bg: '#13B5EA' },
                        { name: 'Sheets', bg: '#34A853' },
                        { name: 'Gmail', bg: '#EA4335' },
                        { name: 'Slack', bg: '#4A154B' },
                        { name: 'Zapier', bg: '#FF4A00' },
                      ].map((app, i) => (
                        <div key={i} className="px-3 py-2 bg-white rounded-full shadow-sm border border-stone-100 flex items-center gap-2">
                          <div className="w-4 h-4 rounded" style={{ background: app.bg }}></div>
                          <span className="text-xs font-medium text-stone-600">{app.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* MORE SERVICES */}
          <section className="py-20 px-6" style={{ background: 'linear-gradient(135deg, #2d4a35 0%, #3d5a45 50%, #4a7c59 100%)' }}>
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Fraunces', serif" }}>
                  More ways we can help
                </h2>
                <p className="text-lg text-white/70 max-w-2xl mx-auto">
                  Custom solutions for when you need something built specifically for your business.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <div id="apps" className="scroll-mt-32 p-8 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6 text-2xl">💻</div>
                  <h3 className="text-xl font-semibold text-white mb-3">Custom Apps</h3>
                  <p className="text-white/70 mb-4">Calculators, booking systems, client portals, and tools built specifically for how you work.</p>
                  <p className="text-emerald-300 font-semibold mb-4">From $2,999</p>
                  <button onClick={() => goToServiceForm('apps')} className="text-white font-medium hover:underline">Get a quote →</button>
                </div>
                
                <div id="databases" className="scroll-mt-32 p-8 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6 text-2xl">📊</div>
                  <h3 className="text-xl font-semibold text-white mb-3">Databases & Reporting</h3>
                  <p className="text-white/70 mb-4">Organise your customers, inventory, jobs, and data. Get insights that help you make decisions.</p>
                  <p className="text-emerald-300 font-semibold mb-4">From $1,499</p>
                  <button onClick={() => goToServiceForm('database')} className="text-white font-medium hover:underline">Get a quote →</button>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-20 px-6 bg-white">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-6" style={{ fontFamily: "'Fraunces', serif" }}>
                Ready to get started?
              </h2>
              <p className="text-xl text-stone-600 mb-8">
                Tell us about your project and we'll get back to you with a free quote within 1-2 business days.
              </p>
              <button 
                onClick={() => setCurrentPage('project')}
                className="px-8 py-4 rounded-full text-white font-semibold text-lg hover:shadow-xl hover:-translate-y-1 transition-all" 
                style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #5d8a64 100%)' }}
              >
                Start a project →
              </button>
              <p className="text-stone-400 text-sm mt-4">Free quotes • Fair pricing • Australia-wide</p>
            </div>
          </section>
        </>
      )}

      {/* ==================== PROJECT FORM PAGE ==================== */}
      {currentPage === 'project' && (
        <section className="pt-28 pb-20 px-6 bg-stone-100 min-h-screen">
          <div className="max-w-3xl mx-auto">
            <button onClick={() => { setCurrentPage('home'); setSelectedService(''); }} className="flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-8 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Back to home
            </button>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Service Selection */}
              <div className="bg-white rounded-2xl p-6 border border-stone-200">
                <h2 className="text-2xl font-bold text-stone-800 mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Start a project</h2>
                <p className="text-stone-500 mb-6">Tell us what you need and we'll get back to you with a quote.</p>
                
                <h3 className="text-lg font-semibold text-stone-800 mb-4">What can we help you with?</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {services.map((service) => (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => setSelectedService(service.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedService === service.id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <span className="text-2xl mb-2 block">{service.icon}</span>
                      <span className="font-medium text-stone-800 text-sm block">{service.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Service-Specific Questions */}
              {selectedService && (
                <>
                  {renderServiceQuestions()}

                  {/* Budget Selection (for services with budgets) */}
                  {getSelectedServiceData()?.budgets.length > 0 && (
                    <div className="bg-white rounded-2xl p-6 border border-stone-200">
                      <h3 className="text-lg font-semibold text-stone-800 mb-4">
                        {selectedService === 'branding' ? 'Which package interests you?' : 'Which package are you interested in?'}
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {getSelectedServiceData()?.budgets.map((budget) => (
                          <label
                            key={budget.value}
                            className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              formData.budget === budget.value
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-stone-200 hover:border-stone-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="budget"
                              value={budget.value}
                              checked={formData.budget === budget.value}
                              onChange={handleInputChange}
                              className="sr-only"
                            />
                            <div className="font-medium text-stone-800 text-sm">{budget.label}</div>
                            <div className="text-xs text-stone-500">{budget.desc}</div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  <div className="bg-white rounded-2xl p-6 border border-stone-200">
                    <h3 className="text-lg font-semibold text-stone-800 mb-4">When do you need this?</h3>
                    <div className="flex flex-wrap gap-2">
                      {['ASAP', 'Next few weeks', '1-2 months', '3+ months', 'No rush'].map((time) => (
                        <label
                          key={time}
                          className={`px-4 py-2 rounded-full border-2 cursor-pointer transition-all text-sm ${
                            formData.timeline === time
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                              : 'border-stone-200 hover:border-stone-300 text-stone-700'
                          }`}
                        >
                          <input
                            type="radio"
                            name="timeline"
                            value={time}
                            checked={formData.timeline === time}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          {time}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="bg-white rounded-2xl p-6 border border-stone-200">
                    <h3 className="text-lg font-semibold text-stone-800 mb-4">Your details</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Your name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                          placeholder="Jane Smith"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Business name</label>
                        <input
                          type="text"
                          name="businessName"
                          value={formData.businessName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                          placeholder="Smith Services"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                          placeholder="jane@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
                          placeholder="0400 000 000"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="bg-white rounded-2xl p-6 border border-stone-200">
                    <h3 className="text-lg font-semibold text-stone-800 mb-4">Anything else?</h3>
                    <textarea
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none resize-none"
                      placeholder="Questions, concerns, specific requirements—anything at all!"
                    />
                    
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-stone-700 mb-3">How would you prefer we contact you?</label>
                      <div className="flex gap-3">
                        {['Email', 'Phone', 'Either'].map((option) => (
                          <label
                            key={option}
                            className={`px-4 py-2 rounded-full border-2 cursor-pointer transition-all text-sm ${
                              formData.preferContact === option.toLowerCase()
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                                : 'border-stone-200 hover:border-stone-300 text-stone-700'
                            }`}
                          >
                            <input
                              type="radio"
                              name="preferContact"
                              value={option.toLowerCase()}
                              checked={formData.preferContact === option.toLowerCase()}
                              onChange={handleInputChange}
                              className="sr-only"
                            />
                            {option}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="text-center pt-4">
                    <button
                      type="submit"
                      className="px-10 py-4 rounded-full text-white font-semibold text-lg transition-all hover:shadow-xl hover:-translate-y-1"
                      style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #5d8a64 100%)' }}
                    >
                      Send enquiry
                    </button>
                    <p className="text-stone-400 text-sm mt-4">We'll get back to you within 1-2 business days.</p>
                  </div>
                </>
              )}
            </form>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 bg-stone-900 text-stone-400">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4a7c59 0%, #7cb77c 100%)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 3C12 3 8 7 8 12C8 15.5 9.5 18 12 20C14.5 18 16 15.5 16 12C16 7 12 3 12 3Z" fill="white" opacity="0.9"/>
                <path d="M12 8C12 8 10 10.5 10 13C10 14.8 10.8 16.2 12 17C13.2 16.2 14 14.8 14 13C14 10.5 12 8 12 8Z" fill="#2d4a35"/>
              </svg>
            </div>
            <span className="text-white font-medium" style={{ fontFamily: "'Fraunces', serif" }}>Offsider HQ</span>
          </div>
          <div className="text-sm">
            © 2026 Offsider HQ · Sydney, Australia
          </div>
        </div>
      </footer>
    </div>
  );
}
